/**
 * NGS mystery-item cart gate.
 *
 * Keeps the free Mystery Item line in sync with the cart:
 *   qualifying subtotal >= threshold  ->  exactly one Mystery Item present
 *   qualifying subtotal <  threshold  ->  no Mystery Item present
 *
 * Config comes from window.SpkMystery, emitted by snippets/scripts.liquid from
 * theme settings. Nothing here is hardcoded — hardcoded thresholds are a named
 * anti-pattern in the shopify-site-engineer skill.
 *
 * ---------------------------------------------------------------------------
 * RENDER COST — why this file works so hard to stay quiet
 * ---------------------------------------------------------------------------
 *
 * cart-items-component has THREE repaint paths, and they are not equal:
 *
 *   its own mutation      -> morphSection(..., {mode: isDrawer ? 'hydration' : 'full'})
 *   event WITH sections   -> morphSection(sectionId, html)            // full mode
 *   event WITHOUT sections-> sectionRenderer.renderSection(...)       // EXTRA FETCH + full
 *
 * 'hydration' only morphs nodes carrying data-hydration-key, so the drawer keeps
 * its DOM, its scroll offset, and any in-flight row animation. 'full' morphs the
 * whole subtree.
 *
 * The first version of this file dispatched a CartUpdateEvent with no sections
 * after every change. That took the worst path: a second network round trip and
 * a full-subtree morph landing a few hundred ms after the theme's own surgical
 * morph — on top of a redundant /cart.js read and a second read after mutating.
 * Five requests and two repaints per interaction, the second one arriving mid
 * row-removal-animation. That is what "glitchy, doesn't flow smoothly" looks like.
 *
 * So, in order of importance:
 *
 *   1. READ THE CART OFF THE EVENT. CartUpdateEvent.detail.resource is already
 *      the new cart. Re-fetching /cart.js to learn what we were just told is a
 *      wasted round trip on every single cart interaction.
 *
 *   2. MUTATE VIA /cart/update.js, ASKING FOR SECTIONS IN THE SAME REQUEST.
 *      It returns the full cart AND the rendered sections, so one request covers
 *      mutate + read + repaint. Passing those sections on the event also keeps
 *      cart-items-component off the extra-fetch path.
 *
 *   3. STAY SILENT WHEN NOTHING CHANGED. Most cart interactions do not cross the
 *      threshold. Those must cost zero requests and zero repaints.
 *
 *   4. COALESCE. The theme debounces quantity input by 300ms but still emits an
 *      event per settled change; a held-down quantity button must not stack
 *      mutations.
 *
 * /cart/update.js also happens to be the correct primitive for a second reason:
 * it sets an ABSOLUTE quantity per variant. Shopify splits one variant across
 * several lines when a discount applies to some units and not others, and an
 * absolute set collapses that case without walking line keys.
 *
 * ---------------------------------------------------------------------------
 * CORRECTNESS — the parts that must not be simplified away
 * ---------------------------------------------------------------------------
 *
 * 1. QUALIFYING SUBTOTAL EXCLUDES THE MYSTERY ITEM ITSELF.
 *    It carries a $75 price that a BXGY discount zeroes out. Measuring the
 *    threshold against cart.total_price would let the item's own price prop the
 *    cart above the line, so removing a real product could never drop it back
 *    under and auto-remove would never fire.
 *
 * 2. final_line_price, NOT original_line_price.
 *    Under-counting is safe (item added late = a missed freebie). Over-counting
 *    charges someone $75 for a "free" item. Fail toward the safe side.
 *
 * 3. THE SERVER IS THE ONLY AUTHORITY ON WHETHER IT IS ACTUALLY FREE.
 *    Only ONE automatic discount applies per cart. This store's ACTIVE "Popup
 *    Welcome 10% Off" code declares combinesWith.productDiscounts = false, so a
 *    shopper holding one CANNOT receive the mystery BXGY — ~1 in 5 recent orders
 *    carried one. After any change, CHECK, and retract if the server did not
 *    zero the line. Never leave an unrequested charge in a cart.
 *
 * 4. THE RETRACTION LATCH RE-ARMS ON GROWTH, NOT ON A FLAT FLAG.
 *    A "tried once, never again" latch strands a shopper who tops up into a
 *    genuinely qualifying cart. Record the subtotal the server refused at and
 *    retry only once the cart grows past that watermark.
 *
 * 5. THE LATCH IS CLEARED ON BFCACHE RESTORE.
 *    Session flags survive Back-button restores because the JS heap is restored
 *    with the page. Without the pageshow handler, a shopper who bounces out of
 *    checkout and back returns to a silently disarmed gate.
 */

import { ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const CFG = window.SpkMystery || {};
const VARIANT_ID = Number(CFG.variantId) || 0;
const THRESHOLD = Number(CFG.thresholdCents) || 0;
const ENABLED = CFG.enabled === true;

/** sessionStorage key holding the qualifying subtotal at which the server last refused. */
const LATCH_KEY = 'spk:mystery:refused-at';

/** Re-entrancy guard. Our own mutations re-enter via the cart:update we dispatch. */
let inFlight = false;
let rerunQueued = false;
/** Coalesces bursts of cart events into one pass. */
let coalesceTimer = null;

/* -------------------------------------------------------------------------- */
/* storage helpers — private mode throws on access, so every call is guarded   */
/* -------------------------------------------------------------------------- */

function readLatch() {
  try {
    const raw = sessionStorage.getItem(LATCH_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

function writeLatch(cents) {
  try {
    sessionStorage.setItem(LATCH_KEY, String(cents));
  } catch {
    /* private mode — the gate still works, it just retries more often */
  }
}

function clearLatch() {
  try {
    sessionStorage.removeItem(LATCH_KEY);
  } catch {
    /* no-op */
  }
}

/* -------------------------------------------------------------------------- */
/* cart access                                                                */
/* -------------------------------------------------------------------------- */

// scripts.liquid declares `const Theme = {...}` in a classic script. A top-level
// const is a global LEXICAL binding — readable as a bare identifier from this
// module, but NOT a property of window. `window.Theme` is undefined here; reading
// it would silently fall back to the hardcoded routes and break localised stores.
const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};

const withJs = (url, fallback) => {
  const base = url || fallback;
  return base.endsWith('.js') ? base : `${base}.js`;
};

const CART_URL = withJs(routes.cart_url, '/cart');
const UPDATE_URL = withJs(routes.cart_update_url, '/cart/update');

async function getCart() {
  const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart read failed: ${res.status}`);
  return res.json();
}

/** Every cart-items section on the page, so one mutation can repaint them all. */
function sectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

/**
 * Set the mystery item to an absolute quantity.
 * Returns the full cart, with `sections` attached when the page had any to render.
 */
async function setMysteryQuantity(quantity) {
  const ids = sectionIds();
  const body = { updates: { [VARIANT_ID]: quantity } };
  if (ids.length) {
    body.sections = ids.join(',');
    body.sections_url = window.location.pathname;
  }
  const res = await fetch(UPDATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`cart update failed: ${res.status}`);
  return res.json();
}

/* -------------------------------------------------------------------------- */
/* cart interrogation                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Every line matching the mystery variant. Shopify SPLITS a line when a discount
 * applies to some units and not others, so one variant can occupy several lines
 * at different prices. Never read only the first match.
 */
function mysteryLines(cart) {
  return (cart.items || []).filter((line) => line.variant_id === VARIANT_ID);
}

/** Sum of every NON-mystery line, post-discount. This is what the threshold tests. */
function qualifyingSubtotal(cart) {
  return (cart.items || [])
    .filter((line) => line.variant_id !== VARIANT_ID)
    .reduce((sum, line) => sum + line.final_line_price, 0);
}

function totalQuantity(lines) {
  return lines.reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * True only when the server charged nothing across EVERY mystery line.
 * If any unit is charged, the discount did not fully apply and the item goes back.
 */
function serverSaysFree(lines) {
  if (!lines.length) return false;
  return lines.reduce((sum, line) => sum + line.final_line_price, 0) === 0;
}

/* -------------------------------------------------------------------------- */
/* repaint                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Announce a change we made, handing over the sections the mutation already
 * rendered. With `sections` present cart-items-component morphs directly; without
 * them it would fire its own Section Rendering request first.
 *
 * This re-enters our own cart:update listener, which ignores events it authored.
 */
function repaint(sections) {
  if (!sections) return;

  /* Morph each cart section OURSELVES, in the same mode the theme uses on its
     own mutations (component-cart-items line 195):

       drawer -> 'hydration'   only nodes carrying data-hydration-key
       page   -> 'full'

     The previous version dispatched a CartUpdateEvent instead. That is a FOREIGN
     event, and cart-items-component handles those on its full-morph branch — the
     whole header subtree replaced, landing after the theme's own surgical morph
     and interrupting the row-removal animation mid-flight. Hydration mode is the
     difference between the drawer updating and the drawer lurching.

     Not dispatching also means this can never pop the drawer open: the drawer's
     add handler is bound to CartAddEvent.eventName, which IS ThemeEvents.cartUpdate,
     so ANY cart:update would have called showDialog(). */
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = el.dataset.sectionId;
    const html = sections[id];
    if (!html) continue;
    try {
      morphSection(id, html, { mode: el.dataset.drawer !== undefined ? 'hydration' : 'full' });
    } catch (e) {
      console.error('[spk-mystery-gate] morph', id, e);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* the gate                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * @param {object|null} knownCart - cart carried by the triggering event, if any.
 *   Passing it avoids a /cart.js round trip on every cart interaction.
 */
async function reconcile(knownCart = null) {
  if (!ENABLED || !VARIANT_ID || THRESHOLD <= 0) return;

  if (inFlight) {
    rerunQueued = true;
    return;
  }
  inFlight = true;

  try {
    // Only trust a handed-over cart if it is a FULL cart. CartAddEvent carries the
    // /cart/add.js response, which is just the lines that were added — it has an
    // `items` array too, so an Array.isArray check happily accepts it and the
    // subtotal comes out as the price of the last thing added. That reads as
    // "below threshold" and yanks the reward straight back out. `item_count` and
    // `total_price` exist only on a real cart, so discriminate on those.
    const isFullCart =
      knownCart &&
      Array.isArray(knownCart.items) &&
      typeof knownCart.item_count === 'number' &&
      typeof knownCart.total_price === 'number';

    let cart = isFullCart ? knownCart : await getCart();
    let lines = mysteryLines(cart);
    const subtotal = qualifyingSubtotal(cart);
    const refusedAt = readLatch();

    const present = totalQuantity(lines);
    let desired;

    if (subtotal < THRESHOLD) {
      // Below threshold: the item must not be here, whatever put it here.
      desired = 0;
      clearLatch();
    } else if (refusedAt !== null && subtotal <= refusedAt) {
      // Server refused at this size before; stay out until the cart grows past it.
      desired = 0;
    } else {
      desired = 1;
    }

    // Most interactions land here: nothing to do, so cost nothing.
    if (present === desired) {
      // Still re-verify a present item — a shopper can apply a non-combining code
      // later, which silently revokes a discount already granted.
      if (desired === 1 && !serverSaysFree(lines)) {
        const result = await setMysteryQuantity(0);
        writeLatch(subtotal);
        repaint(result.sections);
      }
      return;
    }

    const result = await setMysteryQuantity(desired);
    lines = mysteryLines(result);

    // Prove the server actually zeroed it before letting it stand.
    if (desired === 1 && lines.length && !serverSaysFree(lines)) {
      const undone = await setMysteryQuantity(0);
      writeLatch(subtotal);
      repaint(undone.sections);
      return;
    }

    repaint(result.sections);
  } catch (error) {
    console.error('[spk-mystery-gate]', error);
  } finally {
    inFlight = false;
    if (rerunQueued) {
      rerunQueued = false;
      reconcile();
    }
  }
}

/** Collapse a burst of events (held quantity button, rapid removals) into one pass. */
function schedule(knownCart = null) {
  if (coalesceTimer !== null) clearTimeout(coalesceTimer);
  coalesceTimer = setTimeout(() => {
    coalesceTimer = null;
    reconcile(knownCart);
  }, 120);
}

/* -------------------------------------------------------------------------- */
/* wiring                                                                     */
/* -------------------------------------------------------------------------- */

// One listener covers adds too: CartAddEvent.eventName IS ThemeEvents.cartUpdate,
// so an add arrives here as 'cart:update' (carrying only the added lines — see the
// isFullCart guard in reconcile). There is no ThemeEvents.cartAdd member; the
// earlier `addEventListener(ThemeEvents.cartAdd, ...)` resolved to
// addEventListener(undefined) and silently never fired.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  // Ignore the events we dispatch ourselves; the finally-block rerun covers that path.
  if (event?.detail?.data?.source === 'spk-mystery-gate') return;
  schedule(event?.detail?.resource ?? null);
});

// A Back-button restore replays the JS heap, latch included. Clear it so a shopper
// returning from checkout is re-evaluated from scratch.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) clearLatch();
  reconcile();
});

reconcile();
